import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, MailCheck } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6 grain">
      <div className="w-full max-w-sm">
        <Link to="/" className="flex items-center gap-2.5 mb-8"><Logo className="w-7 h-7" /><Wordmark className="font-serif text-xl" /></Link>

        {sent ? (
          <div data-testid="forgot-sent">
            <MailCheck className="w-10 h-10 text-primary mb-4" />
            <h1 className="font-serif text-3xl mb-2">Check your email</h1>
            <p className="text-sm text-muted-foreground mb-8">
              If an account exists for <span className="text-foreground">{email}</span>, we've sent a link to reset your
              password. It expires in one hour. Be sure to check your spam folder.
            </p>
            <Link to="/login" data-testid="forgot-back-login" className="text-sm text-primary hover:underline">← Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <h1 className="font-serif text-4xl mb-2">Forgot password</h1>
            <p className="text-sm text-muted-foreground mb-8">Enter your account email and we'll send you a link to set a new password.</p>

            {error && <div className="mb-4 text-sm text-destructive-foreground bg-destructive/30 border border-destructive px-3 py-2" data-testid="forgot-error">{error}</div>}

            <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Email</label>
            <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-muted border border-input px-3 py-2.5 mb-6 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

            <button data-testid="forgot-submit" disabled={loading}
              className="w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
              {loading && <Loader2 className="w-4 h-4 animate-spin" />} Send reset link
            </button>

            <p className="mt-6 text-sm text-muted-foreground">Remembered it? <Link to="/login" className="text-primary hover:underline">Sign in</Link></p>
          </form>
        )}
      </div>
    </div>
  );
}
